<script setup lang="ts">
import {computed} from 'vue';
import ProgramCatalogueBlock from '@/vue/molecules/today/program-catalogue-block.vue';
import {pagesPath} from '@/ts/router/pages-path';
import type {ProgramCatalogue, TileProgram} from '@/ts/types/component/today-page';

/**
 * Program tile for the bento Today page.
 *
 * The tile is two catalogues — Balm Medical and prevention — resolved
 * independently: a catalogue the user has programs in lists them, one they do
 * not invites them to browse it. With no programs anywhere the tile switches to
 * a split layout of one large card per catalogue that has something to offer —
 * the only state that is not just a combination of the two blocks.
 *
 * Sizing: the tile fills the bento cell it is given — a full row, as the design
 * has it. Its internal layout is driven by container queries, so it lays out
 * from its own width rather than the viewport's and stays correct at whatever
 * width it ends up with.
 *
 * Usage — everything it needs is in the props; it emits nothing:
 *
 * ```vue
 * <program-tile
 *   :programs="programs"
 *   :has-medical-catalogue="available.medical"
 *   :has-prevention-catalogue="available.prevention"
 * />
 * ```
 *
 * Navigation is built in: rows link into the intervention (or its recap once
 * finished) and the browse CTAs link to Paths and Explore, so there is no click
 * handler to wire.
 *
 * `catalogue` on each program decides which block it appears in, and is the
 * caller's job to set. It comes from the payload's own `type`, which the Today
 * endpoint carries — the interventions endpoint this tile once read from does
 * not, which is why everything used to land under Balm Medical.
 *
 * The two `has…Catalogue` props say whether each catalogue has anything to
 * offer AT ALL, which is not the same question as whether the user has programs
 * in it: a licence can offer a catalogue somebody joined nothing of. While one
 * is false its block is hidden rather than inviting the user into a catalogue
 * with nothing behind it. A program the user already has is drawn either way —
 * being wrong about what is left to browse must not take away what they
 * started.
 */
const props = withDefaults(defineProps<{
  programs?: TileProgram[];
  /** Whether the prevention catalogue has anything to offer at all. */
  hasPreventionCatalogue?: boolean;
  /**
   * Whether the Balm Medical catalogue has anything to offer at all.
   *
   * Defaults to true, where its sibling defaults to false, and the asymmetry is
   * the payload's rather than a preference: the block was drawn unconditionally
   * until this prop existed, so a caller reading a payload from before the
   * backend could answer gets what it drew then.
   */
  hasMedicalCatalogue?: boolean;
}>(), {
  programs: () => [],
  hasPreventionCatalogue: false,
  hasMedicalCatalogue: true, // eslint-disable-line vue/no-boolean-default
});

/**
 * What was pressed in the tile (IPD-2974), forwarded rather than measured here: every control in
 * it is a link that navigates on its own, so nothing would be counted otherwise, and what a click
 * means belongs to the page.
 *
 * The catalogue rather than the route it leads to: medical programs are browsed from Paths and
 * prevention ones from Explore today, and that mapping is the app's to change.
 */
const emit = defineEmits<{
  openProgram: [program: TileProgram];
  browseCatalogue: [catalogue: ProgramCatalogue];
}>();

// Anything that is not explicitly prevention lands in the medical block. The
// value is derived by the caller rather than validated by a type at runtime, so
// an unexpected one (a raw backend string, say) must still put the user's
// program on screen instead of dropping it from both blocks.
const prevention = computed(() => props.programs.filter((program) => program.catalogue === 'prevention'));
const medical = computed(() => props.programs.filter((program) => program.catalogue !== 'prevention'));

/**
 * The prevention block is shown when the catalogue exists, or when the user
 * already has a prevention program — data on screen must never be hidden by a
 * flag that says the catalogue is empty.
 */
const showPrevention = computed(() => props.hasPreventionCatalogue || prevention.value.length > 0);

/**
 * The same rule for Balm Medical, which used to be drawn whatever the licence
 * held. A licence with only prevention in it was sent to a page listing nothing.
 *
 * Like its sibling it yields to what is on screen: a program the user is in is
 * never hidden because a flag says its catalogue is empty.
 */
const showMedical = computed(() => props.hasMedicalCatalogue || medical.value.length > 0);

/** With nothing anywhere the tile becomes two invitations rather than two empty blocks. */
const isEmpty = computed(() => props.programs.length === 0);
</script>

<template>
  <section class="program-tile">
    <div class="pt-body">
      <div class="pt-head">
        <h2 class="pt-h">
          {{ $t('program_tile_title') }}
        </h2>
        <p v-if="isEmpty" class="pt-sub">
          {{ $t('program_tile_empty_subtitle') }}
        </p>
      </div>

      <template v-if="isEmpty">
        <!-- Never an empty frame: a licence with nothing in either group has no
             invitation to make, so the row itself goes rather than standing empty. -->
        <div v-if="showMedical || showPrevention" class="pt-split">
          <router-link
            v-if="showMedical"
            class="pt-card"
            :to="pagesPath.intervention.interventionsList"
            @click="emit('browseCatalogue', 'medical')"
          >
            <span class="pt-card-text">
              <span class="pt-card-title">{{ $t('program_tile_medical_label') }}</span>
              <span class="pt-card-sub">{{ $t('program_tile_medical_blurb') }}</span>
            </span>
            <span class="pt-card-cta">
              {{ $t('program_tile_browse_medical') }}
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              ><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </span>
          </router-link>
          <router-link
            v-if="showPrevention"
            class="pt-card"
            :to="pagesPath.main.explore"
            @click="emit('browseCatalogue', 'prevention')"
          >
            <span class="pt-card-text">
              <span class="pt-card-title">{{ $t('program_tile_prevention_label') }}</span>
              <span class="pt-card-sub">{{ $t('program_tile_prevention_blurb') }}</span>
            </span>
            <span class="pt-card-cta">
              {{ $t('program_tile_browse_prevention') }}
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              ><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </span>
          </router-link>
        </div>
      </template>

      <div v-else class="pt-groups">
        <program-catalogue-block
          v-if="showMedical"
          catalogue="medical"
          :programs="medical"
          @open="emit('openProgram', $event)"
          @browse="emit('browseCatalogue', $event)"
        />
        <program-catalogue-block
          v-if="showPrevention"
          catalogue="prevention"
          :programs="prevention"
          @open="emit('openProgram', $event)"
          @browse="emit('browseCatalogue', $event)"
        />
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.program-tile {
  // The design's row surfaces sit slightly warmer than the app's neutral scale
  // (measured #e2dcd7 against the token's #e8e4e0), so the rows read as barely
  // raised off the tile rather than clearly lighter. Its dark values, by
  // contrast, land exactly on the tokens — hence light-only overrides.
  --pt-row: #e2dcd7;
  --pt-row-done: #cbc6c3;
  --pt-sunk: #d0c9c3;
  --pt-outline: rgb(32 40 46 / 22%);
  // The ink hovers mix toward. It follows the theme: mixing a dark surface
  // toward a dark ink is invisible, so dark mode lightens instead.
  --pt-outline-ink: #20282e;
  --pt-hover-ink: #20282e;

  @include dark-mode {
    --pt-outline: rgb(240 243 246 / 22%);
    --pt-outline-ink: #f0f3f6;
    --pt-hover-ink: #f0f3f6;
    --pt-row: var(--Neutral-UI-neutral-ui-10);
    // Dark mode collapses the finished row into the normal one, as the design does.
    --pt-row-done: var(--Neutral-UI-neutral-ui-10);
    --pt-sunk: var(--Neutral-UI-neutral-ui-30);
  }

  border-radius: var(--today-radius-section);
  // The scaffold's raised surface resolves to the same neutral the design
  // measures, and follows the theme on its own.
  background: var(--today-surface-raised);
  color: var(--Neutral-UI-neutral-ui-100);
  // The queries inside size from this element, and a container query cannot
  // style its own container — hence the inner .pt-body.
  container-type: inline-size;
  container-name: program-tile;
}

.pt-body {
  display: flex;
  flex-direction: column;
  gap: 32px;
  // `--today-tile-padding` resolves to 48 on a wide screen; the prototype holds
  // 42 at this very tile width (measured at 1600/1416), so the scale's own 42
  // token is used instead. Raised with Carmelo — if the Today tiles are meant to
  // share one padding, this is the line that changes.
  padding: var(--Spacing-spacing-6);
}

.pt-h {
  margin: 0;
  font-family: var(--Font-Secondary-Font);
  font-size: 30px;
  font-weight: 200;
  line-height: 27px;
  letter-spacing: -0.75px;
}

.pt-head {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pt-sub {
  margin: 0;
  font-family: var(--Font-Primary-Font);
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  // Dimmed in the design, like every secondary line in this tile.
  color: var(--Neutral-UI-neutral-ui-70);
}

.pt-groups {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.pt-split {
  display: flex;
  gap: 16px;
}

.pt-card {
  display: flex;
  text-decoration: none;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  gap: 24px;
  min-height: 150px;
  padding: 24px;
  border-radius: 12px;
  background: var(--Neutral-UI-neutral-ui-10);
  color: var(--Neutral-UI-neutral-ui-100);
  cursor: pointer;
  transition: background 200ms cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    background: color-mix(in srgb, var(--Neutral-UI-neutral-ui-10) 93%, var(--pt-hover-ink, #20282e));
  }

  // Same correction the rows carry, on the same surface and for the same reason: in dark mode the
  // ink is light, so the hover lifts this card until it lands on the tile it sits on (#373F46 over
  // #353F48, a contrast of 1.00) and the card reads as if it had turned transparent. 86% clears it,
  // leaving the hovered card as far from the tile as the resting one is.
  @include dark-mode {
    &:hover {
      background: color-mix(in srgb, var(--Neutral-UI-neutral-ui-10) 86%, var(--pt-hover-ink, #20282e));
    }
  }

  &:hover svg {
    transform: translateX(3px);
  }

  @include focus-visible;
}

.pt-card-text {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pt-card-title {
  font-family: var(--Font-Secondary-Font);
  font-size: 24px;
  font-weight: 300;
  line-height: 24px;
  letter-spacing: -0.6px;
}

.pt-card-sub {
  font-family: var(--Font-Primary-Font);
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  color: var(--Neutral-UI-neutral-ui-70);
}

.pt-card-cta svg {
  transition: transform 200ms cubic-bezier(0.16, 1, 0.3, 1);
}

.pt-card-cta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 6px;
  font-family: var(--Font-Primary-Font);
  font-size: 14px;
  font-weight: 700;
  // Explicit: the inherited 1.6 ratio would make this line 26px and push the
  // card past the design's 150.
  line-height: 21px;
}

@container program-tile (max-width: 700px) {
  .pt-body {
    gap: 28px;
    padding: 36px;
  }

  .pt-groups {
    gap: 28px;
  }
}

// The design stacks these at viewport 767; as a tile width that is about 719.
@container program-tile (max-width: 719px) {
  .pt-split {
    flex-direction: column;
  }

  .pt-card {
    min-height: 0;
  }
}

@container program-tile (max-width: 440px) {
  .pt-body {
    gap: 24px;
    padding: 18px;
  }

  .pt-h {
    font-size: 28px;
  }

  .pt-groups {
    gap: 24px;
  }
}
</style>
